import React, { useState } from 'react';
import { Box, CircularProgress, Tooltip, alpha, useTheme } from '@mui/material';
import { PhotoCamera, ZoomIn } from '@mui/icons-material';

import PersonAvatar from '../common/PersonAvatar';
import PersonPhotoDialog, { PersonPhotoDialogProps } from './PersonPhotoDialog';
import { usePersonPhotoActions } from './usePersonPhotoActions';

/**
 * A person's photo as an avatar you can click — the one to drop in wherever
 * a person is shown. Clicking opens it large (PersonPhotoDialog); with an
 * `owner` the dialog also offers Change / Remove, and a person with no photo
 * goes straight to choosing one. Without a photo or an owner it is a plain
 * avatar.
 */
const PersonPhoto: React.FC<Omit<PersonPhotoDialogProps, 'open' | 'onClose'> & { size?: number }> = ({
  size = 52, ...dialogProps
}) => {
  const { name, photoFilename, owner, onChanged } = dialogProps;
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { busy, choose, fileInput } = usePersonPhotoActions(owner, onChanged);

  const viewable = !!photoFilename;
  if (!viewable && !owner) return <PersonAvatar name={name} size={size} />;

  const label = viewable ? 'View photo' : 'Add photo';
  return (
    <>
      <Tooltip title={label}>
        <Box
          component="button"
          type="button"
          onClick={() => (viewable ? setOpen(true) : choose())}
          disabled={busy}
          aria-label={label}
          sx={{
            position: 'relative', p: 0, border: 0, bgcolor: 'transparent', borderRadius: '50%',
            cursor: 'pointer', flexShrink: 0,
            '&:hover .photo-overlay, &:focus-visible .photo-overlay': { opacity: 1 },
          }}
        >
          <PersonAvatar name={name} photoFilename={photoFilename} size={size} />
          <Box
            className="photo-overlay"
            sx={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.common.black, 0.45), color: 'common.white',
              opacity: busy ? 1 : 0, transition: 'opacity 120ms ease',
            }}
          >
            {busy
              ? <CircularProgress size={size * 0.36} color="inherit" />
              : viewable ? <ZoomIn sx={{ fontSize: size * 0.4 }} /> : <PhotoCamera sx={{ fontSize: size * 0.4 }} />}
          </Box>
        </Box>
      </Tooltip>
      {fileInput}
      <PersonPhotoDialog {...dialogProps} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default PersonPhoto;
